import { IconButton, IconButtonType, Spinner } from "@whitespace/whitespace-ui";
import {
  Card, Col, Row, Badge
} from "react-bootstrap";
import {
  ColumnDirective,
  ColumnsDirective,
  Filter, FilterSettingsModel, GridComponent, Inject, PageSettingsModel, Resize, Sort, Page as Paging, DetailRow
} from "@syncfusion/ej2-react-grids";
import { parseUsingParseInt } from "helpers/stringUtils";
import { makeGridAACompliant } from "modules/SyncfusionAAFixer";
import { useEffect, useState } from "react";
import AuthorisedMaintenanceProps from "interfaces/SiteDiary/AuthorisedMaintenance/AuthorisedMaintenanceProps";
import { convertToDateFromLocalDate } from "helpers/DateUtils";
import { RolesEnum } from "modules/rolesEnum";
import { useStateMachine } from "little-state-machine";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { useNavigate } from "react-router-dom";
import EditTemplate from "./Templates/EditTemplate";
import DetailsTemplate from "./Templates/DetailsTemplate";

const AuthorisedMaintenance = ({
  diarySaved, hwrcSiteId, diaryDate, authorisedMaintenanceDetails
} : AuthorisedMaintenanceProps) => {
  const navigate = useNavigate();
  const { state } = useStateMachine();

  const pageSize: number = parseUsingParseInt(process.env.REACT_APP_DIARY_CARD_PAGE_SIZE);
  const pageSettings: PageSettingsModel = {
    pageSize,
    pageSizes: [5, 10, 20, 50, "All"]
  };

  const addUrl = `${RoutePathsEnum.AuthorisedMaintenanceAdd}${hwrcSiteId && diaryDate
    ? `/${hwrcSiteId}/${diaryDate}` : ""}`;

  const filterSettings: FilterSettingsModel = { type: "Excel" };
  let grid: GridComponent | undefined;
  const now = new Date();

  const editTemplateInstance = EditTemplate;
  const detailsTemplateInstance = DetailsTemplate;

  const [renderingComplete, setRenderingComplete] = useState(false);
  const [isGridRendered, setIsGridRendered] = useState(false);
  const [showSpinner, setShowSpinner] = useState<boolean>(true);

  useEffect(() => {
    if (isGridRendered && renderingComplete) {
      setShowSpinner(false);
    }
  }, [isGridRendered, renderingComplete, setShowSpinner]);

  const gridCreated = () => {
    setIsGridRendered(true);
  };

  const rowDataBound = (args: any) => {
    const expandIcon = args.row.querySelector(".e-detailrowcollapse");
    if (args.data.childData.length === 0 && expandIcon) {
      expandIcon.style.visibility = "hidden";
    }
  };

  const detailTemplate = (props: any) => {
    const rowData = props;
    if (rowData && rowData.childData && rowData.childData.length > 0) {
      return (
        <GridComponent dataSource={rowData.childData}>
          <ColumnsDirective>
            <ColumnDirective
              field="details"
              headerText="Details"
              textAlign="Left"
              width="100%"
            />
          </ColumnsDirective>
        </GridComponent>
      );
    }
    return null;
  };

  const canAmendDiary = diarySaved
  && !(
    diaryDate
    && convertToDateFromLocalDate(diaryDate).toLocaleDateString()
      !== now.toLocaleDateString()
    && state.userRole === RolesEnum.Site
  );

  return (
    <Card>
      <Card.Header>
        <Row>
          <Col xs={8} className="header">
            <h3 data-testid="authorisedMaintenanceHeader">
              Authorised Maintenance
              <Spinner
                animation="grow"
                visible={showSpinner}
                spinnerText="Loading"
                variant="info"
              />
            </h3>
          </Col>
          <Col xs={4} className="right-pane">
            <div className="d-flex">
              {canAmendDiary && (
                <IconButton
                  data-testid="addButtonAuthorisedMaintenance"
                  className="me-2"
                  iconType={IconButtonType.Add}
                  onClick={() => navigate(addUrl)}
                >
                  Add
                </IconButton>
              )}
              <Badge data-testid="authorisedMaintenanceRowCounter">{authorisedMaintenanceDetails.length}</Badge>
            </div>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="no-padding">
        <Row className="mb-2 ms-2 mt-2 me-2 w-auto">
          <Col>
            <GridComponent
              ref={(g: GridComponent) => {
                grid = g;
              }}
              id="gridAuthorisedMaintenance"
              data-testid="gridAuthorisedMaintenance"
              allowPaging
              allowResizing
              pageSettings={pageSettings}
              filterSettings={filterSettings}
              allowSelection={false}
              dataSource={diarySaved && authorisedMaintenanceDetails}
              actionComplete={() => {
                makeGridAACompliant(grid!, () => {
                  setRenderingComplete(true);
                });
              }}
              loadingIndicator={{ indicatorType: "Shimmer" }}
              detailTemplate={detailTemplate}
              rowDataBound={rowDataBound}
              created={gridCreated}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="id"
                  headerText="id"
                  visible={false}
                  isPrimaryKey
                />
                <ColumnDirective
                  data-testid="underTakenBy"
                  field="undertakenBy"
                  headerText="Undertaken By"
                />
                <ColumnDirective
                  data-testid="atwPtwNumber"
                  field="atwPtwNumber"
                  headerText="ATW/PTW Number"
                />
                <ColumnDirective
                  data-testid="details"
                  field="details"
                  headerText="Details"
                  template={detailsTemplateInstance}
                  width={60}
                />
                {canAmendDiary
                && (
                  <ColumnDirective
                    field="id"
                    headerText=""
                    data-testid="edit"
                    allowSorting={false}
                    allowFiltering={false}
                    template={editTemplateInstance}
                    width={28}
                  />
                )}
              </ColumnsDirective>
              <Inject services={[Filter, Paging, Sort, Resize, DetailRow]} />
            </GridComponent>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default AuthorisedMaintenance;
